<template>
  <v-row class="question-preview">
    <v-col v-if="showStem" cols="12" md="6">
      <div
        class="mt-2 pa-2 rounded-lg bg-slightBlue noselect"
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0, scale: 1 }"
        :delay="100"
      >
        <QuestionStemPreview
          :question-text="questionData.text || ''"
          :question-images="questionData.newQuestionImageIds || []"
          :question-image-cols="questionData.newQuestionImageCols || 1"
          :all-img-paths="allImgPaths"
          :admin-edit-mode="adminEditMode"
          @edit-section="emit('edit', $event)"
        />
      </div>

      <slot name="question-rating"></slot>
    </v-col>

    <v-col v-if="showOptions" cols="12" md="6">
      <QuestionOptionsPreview
        :disabled="answersDisabled"
        :options="questionData.options || []"
        :show-results="showResults"
        :chosen-answer-id="chosenAnswerId"
        :all-img-paths="allImgPaths"
        :admin-edit-mode="adminEditMode"
        @answer="emit('answer', $event)"
        @edit-section="emit('edit', $event)"
      >
        <template #option-stats="{ option, index }">
          <slot name="option-stats" :option="option" :index="index"></slot>
        </template>
      </QuestionOptionsPreview>
    </v-col>

    <v-col
      v-if="showResults && showExplanation"
      cols="12"
      v-motion
      :initial="{ opacity: 0, y: 10 }"
      :enter="{ opacity: 1, y: 0, scale: 1 }"
      :delay="100"
    >
      <QuestionExplanationPreview
        :question-specific-explanation-html="
          questionData.questionSpecificExplanationHTML || ''
        "
        :answer-explanation-html="questionData.answerExplanationHTML || ''"
        :answer-title="questionData.answerTitle || ''"
        :universal-images="questionData.universalImages || []"
        :universal-images-cols="questionData.universalImagesCols || 1"
        :universal-image-captions="questionData.universalImageCaptions || {}"
        :answer-images="questionData.newAnswerImageIds || []"
        :answer-image-cols="questionData.newAnswerImageCols || 1"
        :answer-image-captions="questionData.newAnswerImageCaptions || {}"
        :answer-image-location="questionData.answerImageLocation || 'bottom'"
        :all-img-paths="allImgPaths"
        :last-updated-display="explanationLastUpdatedDisplay"
        :admin-edit-mode="adminEditMode"
        @edit-section="emit('edit', $event)"
      >
        <template #rating>
          <slot name="explanation-rating"></slot>
        </template>
      </QuestionExplanationPreview>
    </v-col>
  </v-row>
</template>

<script setup>
import QuestionExplanationPreview from "@/components/question-bank/QuestionExplanationPreview.vue";
import QuestionOptionsPreview from "@/components/question-bank/QuestionOptionsPreview.vue";
import QuestionStemPreview from "@/components/question-bank/QuestionStemPreview.vue";

defineProps({
  answersDisabled: { type: Boolean, default: false },
  questionData: {
    type: Object,
    required: true,
  },
  allImgPaths: {
    type: Object,
    required: true,
  },
  showResults: {
    type: Boolean,
    default: false,
  },
  chosenAnswerId: {
    type: [String, Number],
    default: null,
  },
  explanationLastUpdatedDisplay: {
    type: Object,
    default: null,
  },
  adminEditMode: {
    type: Boolean,
    default: false,
  },
  showStem: {
    type: Boolean,
    default: true,
  },
  showOptions: {
    type: Boolean,
    default: true,
  },
  showExplanation: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["answer", "edit"]);
</script>

<style lang="scss" scoped>
.question-preview :deep(.v-html-content *) {
  color: rgb(var(--v-theme-on-surface)) !important;
}

.question-preview :deep(.v-html-content p) {
  margin: 1em 0 !important;
  line-height: 1.5 !important;
  display: block !important;
  min-height: 1em !important;
}

.question-preview :deep(.v-html-content p:empty) {
  height: 1em !important;
  margin: 1em 0 !important;
  display: block !important;
}

.question-preview :deep(.v-html-content span) {
  display: inline !important;
}

.question-preview :deep(.v-html-content table) {
  border-collapse: collapse !important;
  margin: 1rem 0 !important;
  width: 100% !important;
  min-width: 75px !important;
  table-layout: fixed !important;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
}

.question-preview :deep(.v-html-content th),
.question-preview :deep(.v-html-content td) {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  padding: 8px !important;
  text-align: left !important;
  min-width: 25px !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
}

.question-preview :deep(.v-html-content th) {
  background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
}

.question-preview :deep(.v-html-content ul),
.question-preview :deep(.v-html-content ol) {
  margin: 0.5em 0 !important;
  padding-left: 2em !important;
  list-style-position: outside !important;
}

.question-preview :deep(.v-html-content li) {
  margin: 0.5em 0 !important;
  padding-left: 0.5em !important;
  line-height: 1.4 !important;
}

.question-preview :deep(.v-html-content.tiptap h1),
.question-preview :deep(.v-html-content.tiptap h2),
.question-preview :deep(.v-html-content.tiptap h3),
.question-preview :deep(.v-html-content.tiptap h4),
.question-preview :deep(.v-html-content.tiptap h5),
.question-preview :deep(.v-html-content.tiptap h6) {
  margin-top: 1.5em !important;
  margin-bottom: 0.5em !important;
  line-height: 1.2 !important;
}

.question-preview :deep(.v-html-content.tiptap h1:first-child),
.question-preview :deep(.v-html-content.tiptap h2:first-child),
.question-preview :deep(.v-html-content.tiptap h3:first-child),
.question-preview :deep(.v-html-content.tiptap h4:first-child),
.question-preview :deep(.v-html-content.tiptap h5:first-child),
.question-preview :deep(.v-html-content.tiptap h6:first-child) {
  margin-top: 0 !important;
}

.question-preview :deep(.v-html-content.tiptap > * + *) {
  margin-top: 0.75em !important;
}
</style>
